import { useEffect, useMemo, useState } from 'react';

import { fetchFunctionalHistorySummary } from '../../../api/history';
import { fetchAllRuns } from '../../../api/runs';
import { api } from '../../../utils/apiClient';
import { trackFrontendEvent } from '../../../api/events';
import {
  deriveProjectMode,
  deriveSampleHistoryReady,
  deriveSignalsStepComplete,
  deriveWizardStep,
  getNextRecommendedStep,
  isSampleOnlyLibrary,
  type StartPath,
} from '../utils/onboardingDerivation';

type FunctionalLibraryListResponse = {
  data: Array<{
    id: number;
    test_id: string;
    is_sample_library?: boolean;
    library_source?: string;
    constraints?: Record<string, unknown> | null;
  }>;
  total: number;
};

type ObservabilityConfigResponse = {
  prometheus_url: string;
  prometheus_lookback: string;
  prometheus_timeout: number;
};

export const useOnboardingStatus = ({
  organizationId,
  activeProjectId,
  hasProject,
  startPath,
  startChoiceCommitted,
  showStartOptionPicker,
}: {
  organizationId?: string;
  activeProjectId?: string;
  hasProject: boolean;
  startPath: StartPath;
  startChoiceCommitted: boolean;
  showStartOptionPicker: boolean;
}) => {
  const [functionalLibraryCount, setFunctionalLibraryCount] = useState<number>(0);
  const [sampleLibraryLoaded, setSampleLibraryLoaded] = useState(false);
  const [functionalHistoryCount, setFunctionalHistoryCount] = useState<number>(0);
  const [prometheusUrl, setPrometheusUrl] = useState('');
  const [observabilitySkipped, setObservabilitySkipped] = useState(false);
  const [hasRunSelection, setHasRunSelection] = useState(false);

  const observabilitySkipStorageKey = organizationId
    ? `onboarding_observability_skipped_${organizationId}`
    : null;

  useEffect(() => {
    if (!observabilitySkipStorageKey) {
      return;
    }

    setObservabilitySkipped(localStorage.getItem(observabilitySkipStorageKey) === 'true');
  }, [observabilitySkipStorageKey]);

  useEffect(() => {
    const loadObservability = async () => {
      try {
        const response = await api.get<ObservabilityConfigResponse>(
          '/organization-config/observability'
        );
        setPrometheusUrl(response.prometheus_url || '');
        if (response.prometheus_url && observabilitySkipStorageKey) {
          localStorage.removeItem(observabilitySkipStorageKey);
          setObservabilitySkipped(false);
        }
      } catch {
        setPrometheusUrl('');
      }
    };

    void loadObservability();
  }, [observabilitySkipStorageKey]);

  useEffect(() => {
    const loadLibraryCount = async () => {
      if (!activeProjectId) {
        setFunctionalLibraryCount(0);
        setFunctionalHistoryCount(0);
        setHasRunSelection(false);
        return;
      }

      try {
        const [response, historySummary, runs] = await Promise.all([
          api.get<FunctionalLibraryListResponse>(
            `/functional/library?project_id=${activeProjectId}`
          ),
          fetchFunctionalHistorySummary(activeProjectId),
          fetchAllRuns(activeProjectId, 10, 0, 'functional'),
        ]);
        const tests = response.data || [];
        setFunctionalLibraryCount(response.total || tests.length || 0);
        setSampleLibraryLoaded(isSampleOnlyLibrary(tests));
        setFunctionalHistoryCount(historySummary.records_imported || 0);
        setHasRunSelection((runs.data || []).length > 0);
      } catch {
        setFunctionalLibraryCount(0);
        setSampleLibraryLoaded(false);
        setFunctionalHistoryCount(0);
        setHasRunSelection(false);
      }
    };

    void loadLibraryCount();
  }, [activeProjectId]);

  const hasTestLibrary = functionalLibraryCount > 0;
  const hasHistory = functionalHistoryCount > 0;
  const hasObservability = prometheusUrl.trim().length > 0;
  const projectMode = deriveProjectMode({
    hasTestLibrary,
    sampleLibraryLoaded,
    hasProject,
    startPath,
  });
  const isSampleOnlyProject = projectMode === 'sample' && hasTestLibrary;
  const observabilityStepComplete = hasObservability || observabilitySkipped;
  const isRealProjectMode = projectMode === 'real' && hasTestLibrary;
  const ownDataProjectPendingLibrary = projectMode === 'real' && hasProject && !hasTestLibrary;
  const ownDataModeActive = projectMode === 'real' || ownDataProjectPendingLibrary;
  const canConfigureRealObservability = isRealProjectMode || hasObservability;
  const sampleHistoryReady = deriveSampleHistoryReady(
    projectMode,
    functionalHistoryCount
  );
  const signalsStepComplete = deriveSignalsStepComplete({
    projectMode,
    functionalHistoryCount,
    ownDataModeActive,
    hasHistory,
    hasObservability,
    observabilitySkipped,
  });
  const canShowStartOptions = !startChoiceCommitted || showStartOptionPicker;
  const currentWizardStep = deriveWizardStep({
    canShowStartOptions,
    hasProject,
    hasTestLibrary,
    signalsStepComplete,
    hasRunSelection,
  });
  const shouldPromoteObservabilityAction =
    currentWizardStep === 'signals' &&
    canConfigureRealObservability &&
    !!prometheusUrl.trim();
  const shouldPromoteHistoryAction =
    currentWizardStep === 'signals' &&
    ownDataModeActive &&
    !hasHistory &&
    !shouldPromoteObservabilityAction;

  useEffect(() => {
    if (!organizationId) {
      return;
    }

    const isComplete = currentWizardStep === 'done';
    localStorage.setItem(
      `onboarding_complete_${organizationId}`,
      isComplete ? 'true' : 'false'
    );
    if (isComplete) {
      void trackFrontendEvent('onboarding_completed', { onboarding_step: 'done' });
    }
    window.dispatchEvent(
      new CustomEvent('quantik:onboarding-status', {
        detail: { organizationId, complete: isComplete },
      })
    );
  }, [organizationId, currentWizardStep]);

  const checklist = useMemo(
    () => [
      {
        key: 'project',
        label: 'Create your first project',
        complete: hasProject,
        optional: false,
      },
      {
        key: 'library',
        label: 'Connect your test library',
        complete: hasTestLibrary,
        optional: false,
        detail:
          hasTestLibrary && functionalLibraryCount > 0
            ? sampleLibraryLoaded
              ? 'Sample test library loaded'
              : `${functionalLibraryCount} tests ready`
            : undefined,
      },
      {
        key: 'selection',
        label: 'Run your first selection',
        complete: hasRunSelection,
        optional: false,
      },
      {
        key: 'ci',
        label: 'Integrate with your CI pipeline',
        complete: false,
        optional: true,
      },
      {
        key: 'signals',
        label: 'Add signals',
        complete: signalsStepComplete,
        optional: true,
      },
    ],
    [
      functionalLibraryCount,
      hasProject,
      hasRunSelection,
      hasTestLibrary,
      signalsStepComplete,
      sampleLibraryLoaded,
    ]
  );

  return {
    functionalLibraryCount,
    setFunctionalLibraryCount,
    sampleLibraryLoaded,
    setSampleLibraryLoaded,
    functionalHistoryCount,
    setFunctionalHistoryCount,
    prometheusUrl,
    setPrometheusUrl,
    observabilitySkipped,
    setObservabilitySkipped,
    hasRunSelection,
    setHasRunSelection,
    observabilitySkipStorageKey,
    hasTestLibrary,
    hasHistory,
    hasObservability,
    projectMode,
    isSampleOnlyProject,
    observabilityStepComplete,
    isRealProjectMode,
    ownDataProjectPendingLibrary,
    ownDataModeActive,
    canConfigureRealObservability,
    sampleHistoryReady,
    signalsStepComplete,
    canShowStartOptions,
    currentWizardStep,
    shouldPromoteObservabilityAction,
    shouldPromoteHistoryAction,
    checklist,
    nextRecommendedStep: getNextRecommendedStep(checklist),
  };
};
